import type { Language } from "@/modules/language/utils/languageUtils"
import type { ScoreboardScoreEntity } from "@/modules/scores/scoreForms/entities/scoreboardScoreEntity"
import { getScoreDifficultyWording, ScoreResultType, ScoreType } from "@/modules/scores/scoreTypes"
import { getDisplayedScore, getScoreResultTypeCaption, getScoreTypeLabel } from "@/modules/scores/scoreUtils"
import type { SessionBlockExerciseEntity } from "@/modules/sessions/entities/sessionBlockExerciseEntity"
import type { HUUnit } from "@/modules/utils/unitType"

type ScoreboardIndivViewModel = {
  scoreType: string | null
  displayScoreTypeCaption: string
  displayedScore: string
  displayedScoreCompletionDifficulty: string | null
  isScoreCompletionPersonalRecord: boolean
  displayedScoreSetCompletionList: string[] | null
}

export function createScoreboardIndivViewModel(
  exercise: SessionBlockExerciseEntity,
  score: ScoreboardScoreEntity | null,
  unit: HUUnit,
  language: Language,
): ScoreboardIndivViewModel {
  function getScoreType(): string | null {
    if (exercise.scoreSetConfigurations.length === 0) return null
    if (exercise.scoreSetConfigurations.length === 1 && !!exercise.scoreSetConfigurations[0])
      return getScoreTypeLabel(exercise.scoreSetConfigurations[0].scoreType)
    if (
      exercise.scoreSetConfigurations.length > 1 &&
      exercise.scoringOption &&
      exercise.scoringOption === ScoreResultType.SET_N_FOR_SCOREBOARD
    ) {
      return getScoreTypeLabel(
        exercise.scoreSetConfigurations.find((scoreSet) => scoreSet.useForScoring)?.scoreType ?? ScoreType.NO_SCORE,
      )
    }
    if (
      exercise.scoreSetConfigurations.length > 1 &&
      !!exercise.scoreSetConfigurations[0] &&
      !!exercise.scoringOption &&
      exercise.scoreSetConfigurations.every(
        (scoreSet) => scoreSet.scoreType === exercise.scoreSetConfigurations[0]?.scoreType,
      )
    ) {
      return getScoreTypeLabel(exercise.scoreSetConfigurations[0].scoreType)
    }
    return ""
  }

  function getScoreTypeCaption(): string {
    // All scoreSetConfigurations have the same scoreType -> we display the score result type caption
    if (
      exercise.scoreSetConfigurations.length > 1 &&
      !!exercise.scoringOption &&
      exercise.scoreSetConfigurations.every(
        (scoreSet) => scoreSet.scoreType === exercise.scoreSetConfigurations[0]?.scoreType,
      )
    ) {
      return getScoreResultTypeCaption(exercise.scoringOption, true)
    }

    return ""
  }

  function getScoreCompletionDisplayedScore(): string {
    // No score set completions -> we display the score type
    if (!score?.scoreSetCompletions || score.scoreSetCompletions.length === 0) return getScoreType() ?? ""

    // All scoreSetCompletions have the same scoreType -> we display the score
    if (
      !!score.valueDecimal &&
      !!score.scoreSetCompletions[0] &&
      score.scoreSetCompletions.every(
        (scoreSetCompletion) => scoreSetCompletion.scoreType === score?.scoreSetCompletions?.[0]?.scoreType,
      )
    ) {
      return getDisplayedScore(
        score.valueDecimal,
        score.additionalValueDecimal,
        score.scoreSetCompletions[0]?.scoreType ?? ScoreType.NO_SCORE,
        score.timeCapExceeded ?? false,
        unit,
        language,
      )
    } else if (!!score.valueDecimal && score.scoreSetCompletions.length > 0) {
      const scoreSetConfigurationForScoring = exercise.scoreSetConfigurations.find(
        (scoreSetConfiguration) => scoreSetConfiguration.useForScoring,
      )
      if (!scoreSetConfigurationForScoring) throw new Error("Score set configuration not found")

      const scoreSetCompletion = score.scoreSetCompletions.find(
        (scoreSetCompletion) => scoreSetCompletion.scoreSetConfigurationId === scoreSetConfigurationForScoring.id,
      )
      if (!scoreSetCompletion) throw new Error("Score set completion not found")

      return getDisplayedScore(
        scoreSetCompletion.score ?? 0,
        scoreSetCompletion.scoreAdditional,
        scoreSetCompletion.scoreType,
        scoreSetCompletion.timeCapExceeded ?? false,
        unit,
        language,
      )
    } else {
      return ""
    }
  }

  function getDisplayedScoreCompletionDifficulty(): string | null {
    if (score?.scoreSetCompletions && score?.difficulty) {
      return getScoreDifficultyWording(score?.difficulty)
    }

    return null
  }

  function isScoreCompletionPersonalRecord(): boolean {
    return score?.personalRecord ?? false
  }

  function getScoreSetCompletionList(): string[] | null {
    if (
      score?.scoreSetCompletions &&
      score?.scoreSetCompletions.length > 0 &&
      score.scoreSetCompletions.every((scoreSetCompletion) => !scoreSetCompletion.timeCapExceeded)
    ) {
      return score.scoreSetCompletions.map((scoreSetCompletion) =>
        getDisplayedScore(
          scoreSetCompletion.score ?? 0,
          scoreSetCompletion.scoreAdditional,
          scoreSetCompletion.scoreType,
          scoreSetCompletion.timeCapExceeded ?? false,
          unit,
          language,
        ),
      )
    }
    return null
  }

  return {
    scoreType: getScoreType(),
    displayScoreTypeCaption: getScoreTypeCaption(),
    displayedScore: getScoreCompletionDisplayedScore(),
    displayedScoreCompletionDifficulty: getDisplayedScoreCompletionDifficulty(),
    isScoreCompletionPersonalRecord: isScoreCompletionPersonalRecord(),
    displayedScoreSetCompletionList: getScoreSetCompletionList(),
  }
}
